/**
 * Micro-object foundation kits — shared primitive-spec vocabulary.
 *
 * A kit is PURE LAYOUT MATH: functions in this directory take parameters and
 * return arrays of plain primitive SPECS (this module's types) plus the
 * block's measured bounds. No fabric import anywhere under kits/ — that is
 * the law that keeps every kit unit-testable in bare node (tsx). The fabric
 * construction happens exactly once, recipe-side, in ../assemble.ts, which
 * maps each spec kind onto the stock primitive it names:
 *
 *   kind      fabric primitive     notes
 *   ------    -----------------    -------------------------------------
 *   text      FabricText           optional `param` → dblclick edit tag
 *   line      Line                 stroke geometry (rules, leaders)
 *   rect      Rect                 frames, slots, carry boxes
 *   path      Path                 brackets, arrowheads, bubbles ONLY —
 *                                  "simple Path where unavoidable"
 *   circle    Circle               dots, badges (stock primitive)
 *
 * All of these are stock fabric types (types.ts HARD RULE), so every
 * assembled micro-object stays plain fabric JSON — the exportability law.
 *
 * COORDINATES: every kit block uses a local origin — (0,0) is the block's
 * top-left — and reports its own width/height. Recipes compose blocks by
 * shifting spec arrays with translateSpecs() and grouping; fabric's Group
 * constructor derives the bbox, so interior offsets never leak into
 * placement (same convention as every existing recipe).
 *
 * TEXT MEASUREMENT: layout that depends on rendered text width (the fraction
 * bar law, digit columns, word-bank wrap) takes a TextMeasurer FUNCTION.
 * Recipes pass a real canvas-TextMetrics measurer (the handwriting-lines
 * probe pattern); node unit tests pass a deterministic fake. The layout math
 * itself stays pure either way.
 */

/** Measured advance width of `text` rendered at `fontSize`, in px. */
export type TextMeasurer = (text: string, fontSize: number) => number;

/**
 * Fabric's own vertical text-layout constants (Text/constants.mjs) — the same
 * numbers handwriting-lines documents. For a single-line FabricText with
 * uniform styling:
 *
 *   box height       = fontSize * FABRIC_FONT_SIZE_MULT
 *   baseline - top   = fontSize * FABRIC_FONT_SIZE_MULT * (1 - FABRIC_FONT_SIZE_FRACTION)
 *
 * Both depend only on fontSize — never on the face — so pure kit math can
 * place text boxes and baselines exactly, for any font the teacher picks.
 */
export const FABRIC_FONT_SIZE_MULT = 1.13;
export const FABRIC_FONT_SIZE_FRACTION = 0.222;
/** Height of a single-line FabricText box at fontSize 1. */
export const TEXT_BOX_HEIGHT = FABRIC_FONT_SIZE_MULT;
/** Alphabetic-baseline offset from the box top at fontSize 1. */
export const BASELINE_FROM_TOP = FABRIC_FONT_SIZE_MULT * (1 - FABRIC_FONT_SIZE_FRACTION);

export interface TextSpec {
  kind: 'text';
  text: string;
  x: number;
  y: number;
  fontSize: number;
  fontFamily?: string;
  fill?: string;
  fontWeight?: string | number;
  fontStyle?: string;
  originX?: 'left' | 'center' | 'right';
  originY?: 'top' | 'center' | 'bottom';
  /** Rotation in degrees (labels riding an arrow shaft). */
  angle?: number;
  /**
   * Print-key / answer-visibility flag: false renders (and exports) nothing
   * while the text still round-trips in the JSON — flipping it back is the
   * "answer key" print. Absent = visible.
   */
  visible?: boolean;
  /**
   * Dblclick edit-in-place: the name of the ONE scalar recipe param this
   * text renders. The assembler stamps data.tableCell = {kind:'param', key}
   * on the primitive; the editor's existing overlay-edit machinery then
   * writes the committed text back to params[key] and regenerates in place
   * (one history entry — the smart-table contract, generalized).
   */
  param?: string;
}

export interface LineSpec {
  kind: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  stroke: string;
  strokeWidth: number;
  lineCap?: 'butt' | 'round' | 'square';
  dash?: number[];
}

export interface RectSpec {
  kind: 'rect';
  x: number;
  y: number;
  width: number;
  height: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  /** Corner radius (rx = ry). */
  rx?: number;
  dash?: number[];
}

export interface PathSpec {
  kind: 'path';
  /** SVG path data in block-local coordinates. */
  path: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  lineCap?: 'butt' | 'round' | 'square';
  lineJoin?: 'miter' | 'round' | 'bevel';
}

export interface CircleSpec {
  kind: 'circle';
  cx: number;
  cy: number;
  r: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

export type PrimitiveSpec = TextSpec | LineSpec | RectSpec | PathSpec | CircleSpec;

/** Every kit function returns at least this: the specs and their bounds. */
export interface KitBlock {
  specs: PrimitiveSpec[];
  width: number;
  height: number;
}

/**
 * Shift a spec array by (dx, dy) — the composition primitive recipes use to
 * place kit blocks side by side. Returns NEW specs; never mutates input
 * (kits and recipes may reuse a block).
 */
export function translateSpecs(specs: PrimitiveSpec[], dx: number, dy: number): PrimitiveSpec[] {
  return specs.map((s) => {
    switch (s.kind) {
      case 'text':
        return { ...s, x: s.x + dx, y: s.y + dy };
      case 'line':
        return { ...s, x1: s.x1 + dx, y1: s.y1 + dy, x2: s.x2 + dx, y2: s.y2 + dy };
      case 'rect':
        return { ...s, x: s.x + dx, y: s.y + dy };
      case 'circle':
        return { ...s, cx: s.cx + dx, cy: s.cy + dy };
      case 'path':
        return { ...s, path: translatePathData(s.path, dx, dy) };
    }
  });
}

/**
 * Shift absolute SVG path data by (dx, dy). Kits emit ONLY absolute
 * commands from this closed set: M L H V Q C A Z. H/V carry a single axis;
 * A shifts only its endpoint (rx ry rot large-arc sweep stay put).
 * Lowercase (relative) commands are already position-independent and pass
 * through untouched.
 */
export function translatePathData(d: string, dx: number, dy: number): string {
  const tokens = d.match(/[A-Za-z]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) || [];
  const out: string[] = [];
  let cmd = '';
  let i = 0;
  const fmt = (n: number): string => String(Math.round(n * 1000) / 1000);
  while (i < tokens.length) {
    const t = tokens[i];
    if (/[A-Za-z]/.test(t)) {
      cmd = t;
      out.push(t);
      i += 1;
      continue;
    }
    switch (cmd) {
      case 'M':
      case 'L':
      case 'T': {
        out.push(fmt(Number(tokens[i]) + dx), fmt(Number(tokens[i + 1]) + dy));
        i += 2;
        break;
      }
      case 'H': {
        out.push(fmt(Number(tokens[i]) + dx));
        i += 1;
        break;
      }
      case 'V': {
        out.push(fmt(Number(tokens[i]) + dy));
        i += 1;
        break;
      }
      case 'Q':
      case 'S': {
        out.push(
          fmt(Number(tokens[i]) + dx),
          fmt(Number(tokens[i + 1]) + dy),
          fmt(Number(tokens[i + 2]) + dx),
          fmt(Number(tokens[i + 3]) + dy),
        );
        i += 4;
        break;
      }
      case 'C': {
        out.push(
          fmt(Number(tokens[i]) + dx),
          fmt(Number(tokens[i + 1]) + dy),
          fmt(Number(tokens[i + 2]) + dx),
          fmt(Number(tokens[i + 3]) + dy),
          fmt(Number(tokens[i + 4]) + dx),
          fmt(Number(tokens[i + 5]) + dy),
        );
        i += 6;
        break;
      }
      case 'A': {
        out.push(
          tokens[i], tokens[i + 1], tokens[i + 2], tokens[i + 3], tokens[i + 4],
          fmt(Number(tokens[i + 5]) + dx),
          fmt(Number(tokens[i + 6]) + dy),
        );
        i += 7;
        break;
      }
      default: {
        // Relative command params (or Z stragglers) pass through unchanged.
        out.push(tokens[i]);
        i += 1;
      }
    }
  }
  return out.join(' ').replace(/([A-Za-z]) /g, '$1 ').trim();
}

/** Clamp helper shared by every kit (mirrors the recipe convention). */
export const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

/**
 * Deterministic fallback measurer for environments without canvas (node unit
 * tests, SSR safety). ~0.6em per char is the classic average-advance
 * estimate for UI faces; layout LAWS (max / alignment / wrap) hold under any
 * monotonic measurer, which is what the tests assert.
 */
export const estimateMeasure: TextMeasurer = (text, fontSize) => text.length * fontSize * 0.6;
